import { useState } from 'react';
import { changeMemberStatus, type MemberDetailContract } from '@urbanik/api-client';
import {
  Button,
  SelectField,
  TextareaField,
  TextField,
  FormSection,
  FormActions,
  InfoPanel,
  ConfirmationDialog,
  SummaryList,
  SummaryListItem,
  formatDateTime,
} from '@urbanik/ui';
import { Save } from 'lucide-react';
import { apiBase } from './api-base';
import { ResignationDocuments } from './ResignationDocuments';
export function MemberStatusEditor({
  member,
  onChanged,
}: {
  member: MemberDetailContract;
  onChanged: (member: MemberDetailContract) => void;
}) {
  const [operation, setOperation] = useState(
    member.status === 'inactive' ? 'document' : 'inactive',
  );
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [submittedOn, setSubmittedOn] = useState('');
  const [approvedOn, setApprovedOn] = useState('');
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const target = operation === 'active' ? 'active' : 'inactive';
  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await changeMemberStatus(apiBase, member.id, {
        targetStatus: target,
        expectedVersion: member.version,
        reason,
        reasonOnly: operation === 'document',
        ...(target === 'inactive' ? { reasonCode: code } : {}),
        ...(target === 'inactive' && code === 'resignation'
          ? { resignationSubmittedOn: submittedOn, resignationApprovedOn: approvedOn }
          : {}),
      });
      onChanged(response.member);
      setReason('');
      setCode('');
      setOperation(response.member.status === 'inactive' ? 'document' : 'inactive');
      setNotice('Estado y motivo registrados en el historial.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos registrar el cambio.');
    } finally {
      setBusy(false);
      setReview(false);
    }
  };
  return (
    <>
      <InfoPanel title={`Estado actual: ${member.status === 'active' ? 'Activo' : 'Inactivo'}`}>
        Los inactivos se conservan en el padrón histórico. Registrar una causa no reasigna números,
        no cambia titulares ni cancela obligaciones.
      </InfoPanel>
      {error ? <InfoPanel tone="danger">{error}</InfoPanel> : null}
      {notice ? <InfoPanel tone="success">{notice}</InfoPanel> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setReview(true);
        }}
      >
        <FormSection
          title="Estado y motivo administrativo"
          description="Todo cambio conserva los registros anteriores y exige una justificación."
        >
          <SelectField
            label="Operación"
            value={operation}
            disabled={busy}
            onValueChange={setOperation}
            options={
              member.status === 'inactive'
                ? [
                    { value: 'document', label: 'Registrar o corregir causa de inactividad' },
                    { value: 'active', label: 'Reactivar socio' },
                  ]
                : [{ value: 'inactive', label: 'Inactivar socio' }]
            }
          />
          {target === 'inactive' ? (
            <SelectField
              label="Causa de inactividad"
              value={code}
              disabled={busy}
              onValueChange={setCode}
              options={[
                { value: '', label: 'Selecciona una causa' },
                { value: 'resignation', label: 'Renuncia' },
                { value: 'replacement', label: 'Cambio de socio / titular' },
                { value: 'death', label: 'Fallecimiento' },
                { value: 'suspension', label: 'Suspensión' },
                { value: 'other', label: 'Otra causa documentada' },
              ]}
            />
          ) : null}
          {target === 'inactive' && code === 'resignation' ? (
            <>
              <TextField
                label="Fecha de presentación de la renuncia"
                type="date"
                required
                value={submittedOn}
                onChange={(e) => setSubmittedOn(e.target.value)}
                disabled={busy}
              />
              <TextField
                label="Fecha de aprobación de la renuncia"
                type="date"
                required
                min={submittedOn || undefined}
                value={approvedOn}
                onChange={(e) => setApprovedOn(e.target.value)}
                disabled={busy}
                hint="El socio responde por cuotas hasta el mes de presentación, inclusive."
              />
            </>
          ) : null}
          <TextareaField
            label="Motivo y referencia documental"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            minLength={10}
            maxLength={500}
            disabled={busy}
            hint="Indica hechos y, cuando exista, acta, solicitud o referencia del cambio de titular. No supongas causas de la importación."
          />
        </FormSection>
        <FormActions>
          <Button
            type="submit"
            variant="primary"
            icon={<Save aria-hidden="true" />}
            disabled={
              busy ||
              reason.trim().length < 10 ||
              (target === 'inactive' && !code) ||
              (code === 'resignation' && (!submittedOn || !approvedOn || approvedOn < submittedOn))
            }
          >
            Revisar estado y motivo
          </Button>
        </FormActions>
      </form>
      {target === 'inactive' && code === 'resignation' ? (
        <InfoPanel>
          Al confirmar la renuncia aparecerá su sección de constancias. Podrás adjuntar uno o varios
          PDF sin volver a registrar el cambio de estado.
        </InfoPanel>
      ) : null}
      <SummaryList title="Historial de estado">
        {member.history?.map((h) => (
          <SummaryListItem
            key={h.id}
            label={`${h.status === 'active' ? 'Activo' : 'Inactivo'} · ${formatDateTime(h.occurredAt)}`}
            value={h.reason}
          />
        ))}
      </SummaryList>
      {member.history
        ?.filter((h) => h.reasonCode === 'resignation')
        .map((h) => (
          <ResignationDocuments
            key={h.id}
            memberId={member.id}
            historyId={h.id}
            recordedAt={h.occurredAt}
          />
        ))}
      <ConfirmationDialog
        open={review}
        busy={busy}
        title="Confirmar estado y motivo"
        description={`${operation === 'document' ? 'Se conservará al socio inactivo y se añadirá un registro al historial' : target === 'active' ? 'Se reactivará al socio' : 'Se inactivará al socio'}. Motivo: ${reason}`}
        onCancel={() => {
          if (!busy) setReview(false);
        }}
        onConfirm={() => void save()}
      />
    </>
  );
}
